import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import { apiFetch, apiDownload } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatCurrency, formatQuantity, formatNumber } from '../../../../utils/formatters';
import { useBloccoRecordContesto } from '../../../../hooks/useBloccoRecordContesto';
import { usePermesso } from '../../../../context/PermessiContext';
import BadgeFlussoCommerciale, { FlussoCommerciale } from '../../../../components/BadgeFlussoCommerciale';
import { API_DOCS } from './SchedaOrdine.api';
import ModalEsplosioneRiga from './ModalEsplosioneRiga';
import ModalConfigurazioneRigaOrdine from './ModalConfigurazioneRigaOrdine';
import ModalContrattualizzazioneRiga from './ModalContrattualizzazioneRiga';
import ModalEstraiRighe, { RigaEstratta } from '../distinte/ModalEstraiRighe';
import ModalGeneraDdt from '../ddt/ModalGeneraDdt';
import PrintModal from '../../../../components/PrintModal';
import './SchedaOrdine.css';

// ── Tipi ────────────────────────────────────────────────────────────────────
type StatoOrdine = 'BOZZA' | 'CONFERMATO' | 'IN_LAVORAZIONE' | 'CHIUSO';
type ModalitaEvasione = 'SAL' | 'DDT' | 'RAPPORTINO' | 'MISTA';
type DestinatarioOrdine = 'IMPRESA' | 'ACQUIRENTE';
type TipoRigaOrdine = 'ARTICOLO' | 'ARTICOLO_CONFIGURATO' | 'DESCRITTIVA' | 'A_CORPO' | 'RICORRENTE';
type ModalitaConfigurazione = 'DA_MODELLO' | 'DA_PRECONFIGURATO' | 'DIFFERITA_IN_ORDINE' | 'MANUALE';
type ApproccioCalcolo = 'NESSUNO' | 'COSTO_RICARICATO' | 'PREZZO_LISTINO_SCONTATO';
type TipoRigaCapitolato = 'NON_CAPITOLATO' | 'BASE' | 'EXTRA_CAPITOLATO_SOSTITUTIVO' | 'EXTRA_CAPITOLATO_AGGIUNTIVO';
type SottotipoRicorrenza = 'LICENZA' | 'CANONE' | 'CONSUMI' | 'COMMISSIONI';

interface Raggruppamento {
  id: number;
  ordineId: number;
  livello: number;
  parentId: number | null;
  ordinePos: number;
  etichetta: string;
  descrizione: string | null;
}

interface ComponenteRiga {
  id: number;
  ordinePos: number;
  tipologia: string;
  categoriaMerceologicaId: number | null;
  isPrincipale: boolean;
  isInDescrizione: boolean;
  articoloId: number | null;
  articolo?: { id: number; codice: string; descrizioneBase: string; tipologia?: string } | null;
  codicePrecopy: string | null;
  descrizione: string;
  umVendita: string | null;
  quantita: string | null;
  sfrido: string | null;
  costoUnitario: string | null;
  prezzoVenditaUnitario: string | null;
}

interface RigaOrdine {
  id: number;
  ordineId: number;
  raggruppamentoId: number | null;
  ordinePos: number;
  tipoRiga: TipoRigaOrdine;
  alternativaGruppo: string | null;
  alternativaScelta: boolean;
  etichettaAlternativa: string | null;
  articoloId: number | null;
  articolo?: { id: number; codice: string; descrizioneBase: string; tipologia?: string } | null;
  codicePrecopy: string | null;
  descrizione: string;
  descrizioneEstesa: string | null;
  umVendita: string | null;
  quantita: string | null;
  prezzoUnitario: string | null;
  prezzoCalcolato: string | null;
  sconto: string | null;
  importoNetto: string | null;
  codiceIvaId: number | null;
  codiceIva?: { id: number; codice: string; aliquota: string } | null;
  prezzoListino: string | null;
  scontoAcquistoCodice: string | null;
  scontoAcquistoPercentuale: string | null;
  scontoVenditaCodice: string | null;
  scontoVenditaPercentuale: string | null;
  prezzoSovrascritto: boolean;
  modalitaConfigurazione: ModalitaConfigurazione | null;
  modelloConfigurazioneId: number | null;
  articoloPrecConfiguratoId: number | null;
  approccioCalcolo: ApproccioCalcolo | null;
  percentualeRicaricoRiga: string | null;
  scontoSuCalcolato: string | null;
  tipoRigaCapitolato: TipoRigaCapitolato;
  offertaCapRigaId: number | null;
  offertaCapRigaAccessoriaId: number | null;
  scorporoCapitolato: string | null;
  deltaCalcolato: string | null;
  extraAcquirenteCalcolato: string | null;
  importoImpresaCalcolato: string | null;
  sottotipoRicorrenza: SottotipoRicorrenza | null;
  periodicita: string | null;
  durataMesi: number | null;
  dataInizio: string | null;
  dataFine: string | null;
  modalitaPagamento: string | null;
  tariffaUnitaria: string | null;
  tariffaFissaTransazione: string | null;
  percentualeTransazione: string | null;
  prodottoId: number | null;
  prodotto?: { id: number; codice: string; nome: string } | null;
  daEsplodere: boolean;
  daConfigurare: boolean;
  daContrattualizzare: boolean;
  esplosaAt: string | null;
  esplosaById: number | null;
  configurataAt: string | null;
  configurataById: number | null;
  preventivoRigaOrigineId: number | null;
  componenti: ComponenteRiga[];
  contratto?: { id: number; numero: string; stato: string; dataInizio: string; dataFine: string } | null;
}

interface AllegatoOrdine {
  id: number;
  nome: string;
  nomeFile: string;
  mimeType: string | null;
  dimensione: number | null;
  note: string | null;
  visibileAcquirente: boolean;
  uploadedById: number;
  uploadedBy?: { id: number; nome: string | null; email?: string } | null;
  uploadedAt: string;
}

interface OrdineLogEntry {
  id: number;
  utenteId: number;
  utenteName: string;
  azione: string;
  dettaglio: string | null;
  createdAt: string;
}

interface OrdineDettaglio {
  id: number;
  numero: string;
  societaId: number;
  commessaId: number;
  commessa?: {
    id: number;
    numero: string;
    descrizione: string | null;
    clienteRagioneSociale?: string | null;
    sedeId?: number | null;
  };
  dataOrdine: string;
  dataConferma: string | null;
  dataChiusura: string | null;
  descrizione: string | null;
  note: string | null;
  stato: StatoOrdine;
  flussoCommerciale: FlussoCommerciale | null;
  modalitaEvasione: ModalitaEvasione | null;
  destinatarioOrdine: DestinatarioOrdine | null;
  isSoloImporto: boolean;
  acquirenteId: number | null;
  acquirente?: {
    id: number;
    unitaCodice: string | null;
    unitaDescrizione?: string | null;
    nome: string | null;
    cognome: string | null;
  } | null;
  preventivoOrigineId: number | null;
  clienteRagioneSociale: string | null;
  clienteNome: string | null;
  clienteCognome: string | null;
  clientePartitaIva: string | null;
  clienteCodiceFiscale: string | null;
  clienteIndirizzo: string | null;
  clienteCap: string | null;
  clienteCitta: string | null;
  clienteProvincia: string | null;
  clienteEmail: string | null;
  clienteTelefono: string | null;
  indirizzoLavori: string | null;
  scontoGlobale: string | null;
  scontoGlobaleDescrizione: string | null;
  totaleImponibile: string | null;
  totaleIva: string | null;
  totale: string | null;
  codiceIvaDefaultId: number;
  codiceIvaDefault?: { id: number; codice: string; descrizione: string | null; aliquota: string } | null;
  condizionePagamentoId: number | null;
  condizionePagamento?: { id: number; nome: string } | null;
  createdById: number;
  createdBy?: { id: number; nome: string | null; email: string } | null;
  createdAt: string;
  raggruppamenti: Raggruppamento[];
  righe: RigaOrdine[];
  allegati: AllegatoOrdine[];
  log?: OrdineLogEntry[];
  logs?: OrdineLogEntry[];
  accontoPrevisto: boolean;
  accontoImponibileFreeze: string | null;
  accontoIvaFreeze: string | null;
  accontoTotaleFreeze: string | null;
  accontoPercentualeFreeze: string | null;
  accontoCondPagamentoId: number | null;
  accontoCondPagamento?: { id: number; nome: string } | null;
  proformeAcconto?: Array<{
    id: number;
    numero: string;
    origine: 'DA_CONVERSIONE_ORDINE' | 'MANUALE_COMMESSA';
    stato: 'DA_INCASSARE' | 'PAGATA' | 'ANNULLATA';
    imponibile: string;
    iva: string;
    totale: string;
    dataEmissione: string;
    dataPagamento: string | null;
  }>;
}

// ── Helpers di formattazione ────────────────────────────────────────────────
function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('it-IT').format(new Date(value));
  } catch {
    return '—';
  }
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Intl.DateTimeFormat('it-IT', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  } catch {
    return '—';
  }
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || isNaN(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function nz(v: string | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

const ETICHETTE_TIPO_RIGA: Record<TipoRigaOrdine, string> = {
  ARTICOLO: 'Articolo',
  ARTICOLO_CONFIGURATO: 'Articolo conf.',
  DESCRITTIVA: 'Descrittiva',
  A_CORPO: 'A corpo',
  RICORRENTE: 'Ricorrente',
};

const ETICHETTE_STATO: Record<StatoOrdine, string> = {
  BOZZA: 'Bozza',
  CONFERMATO: 'Confermato',
  IN_LAVORAZIONE: 'In lavorazione',
  CHIUSO: 'Chiuso',
};

const ETICHETTE_MODALITA_EVASIONE: Record<ModalitaEvasione, string> = {
  SAL: 'SAL',
  DDT: 'DDT',
  RAPPORTINO: 'Rapportino',
  MISTA: 'Mista',
};

const ETICHETTE_DESTINATARIO: Record<DestinatarioOrdine, string> = {
  IMPRESA: 'Impresa',
  ACQUIRENTE: 'Acquirente',
};

// ── Mini-componenti UI ──────────────────────────────────────────────────────

const BadgeStatoOrdine: React.FC<{ stato: StatoOrdine }> = ({ stato }) => {
  const mod: Record<StatoOrdine, string> = {
    BOZZA: 'bop-badge--neutral',
    CONFERMATO: 'bop-badge--info',
    IN_LAVORAZIONE: 'bop-badge--warning',
    CHIUSO: 'bop-badge--success',
  };
  return <span className={`bop-badge ${mod[stato]}`}>{ETICHETTE_STATO[stato]}</span>;
};

const BadgeModalitaEvasione: React.FC<{ modalita: ModalitaEvasione }> = ({ modalita }) => (
  <span className="bop-badge bop-badge--neutral">Evasione: {ETICHETTE_MODALITA_EVASIONE[modalita]}</span>
);

const BadgeDestinatario: React.FC<{ destinatario: DestinatarioOrdine }> = ({ destinatario }) => (
  <span className="bop-badge bop-badge--primary">Destinatario: {ETICHETTE_DESTINATARIO[destinatario]}</span>
);

interface CampoProps {
  label: string;
  value: React.ReactNode;
  colspan?: number;
}
const Campo: React.FC<CampoProps> = ({ label, value, colspan }) => {
  const cls = colspan === 2 ? 'bop-scheda-ordine__campo bop-scheda-ordine__campo--full' : 'bop-scheda-ordine__campo';
  return (
    <div className={cls}>
      <div className="bop-scheda-ordine__campo-label">{label}</div>
      <div className="bop-scheda-ordine__campo-value">{value == null || value === '' ? '—' : value}</div>
    </div>
  );
};

// ── Drill-down riga: componenti / ricorrenza / capitolato ───────────────────

const DrillDownComponenti: React.FC<{ componenti: ComponenteRiga[] }> = ({ componenti }) => {
  if (!componenti || componenti.length === 0) {
    return <div className="bop-text-muted">Nessun componente.</div>;
  }
  return (
    <div className="bop-scheda-ordine__drilldown-block">
      <h4 className="bop-scheda-ordine__drilldown-title">Componenti</h4>
      <table className="bop-table bop-scheda-ordine__sub-table">
        <thead>
          <tr>
            <th>Pos</th>
            <th>Articolo</th>
            <th>Descrizione</th>
            <th>U.M.</th>
            <th className="bop-text-right">Q.tà</th>
            <th className="bop-text-right">Sfrido %</th>
            <th className="bop-text-right">Costo unit.</th>
            <th className="bop-text-right">Prezzo vend. unit.</th>
            <th>Flag</th>
          </tr>
        </thead>
        <tbody>
          {componenti.map((c) => (
            <tr key={c.id}>
              <td>{c.ordinePos}</td>
              <td>{c.articolo?.codice ?? c.codicePrecopy ?? '—'}</td>
              <td>{c.descrizione}</td>
              <td>{c.umVendita ?? '—'}</td>
              <td className="bop-text-right">{formatQuantity(nz(c.quantita))}</td>
              <td className="bop-text-right">{c.sfrido ? formatNumber(nz(c.sfrido)) : '—'}</td>
              <td className="bop-text-right">{formatCurrency(nz(c.costoUnitario))}</td>
              <td className="bop-text-right">{formatCurrency(nz(c.prezzoVenditaUnitario))}</td>
              <td>
                {c.isPrincipale && <span className="bop-badge bop-badge--info">Principale</span>}
                {c.isInDescrizione && <span className="bop-badge bop-badge--neutral">In descrizione</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DrillDownRicorrenza: React.FC<{ riga: RigaOrdine }> = ({ riga }) => (
  <div className="bop-scheda-ordine__drilldown-block">
    <h4 className="bop-scheda-ordine__drilldown-title">Ricorrenza freeze</h4>
    <div className="bop-scheda-ordine__griglia">
      <Campo label="Sottotipo" value={riga.sottotipoRicorrenza ?? '—'} />
      <Campo label="Periodicità" value={riga.periodicita ?? '—'} />
      <Campo label="Durata (mesi)" value={riga.durataMesi ?? '—'} />
      <Campo label="Data inizio" value={formatDate(riga.dataInizio)} />
      <Campo label="Data fine" value={formatDate(riga.dataFine)} />
      <Campo label="Modalità pagamento" value={riga.modalitaPagamento ?? '—'} />
      <Campo
        label="Tariffa unitaria"
        value={riga.tariffaUnitaria ? formatCurrency(nz(riga.tariffaUnitaria)) : '—'}
      />
      <Campo
        label="Tariffa fissa transazione"
        value={riga.tariffaFissaTransazione ? formatCurrency(nz(riga.tariffaFissaTransazione)) : '—'}
      />
      <Campo
        label="Percentuale transazione"
        value={riga.percentualeTransazione ? `${formatNumber(nz(riga.percentualeTransazione))} %` : '—'}
      />
      <Campo
        label="Prodotto"
        value={
          riga.prodotto ? `${riga.prodotto.codice} — ${riga.prodotto.nome}` : '—'
        }
      />
      {riga.contratto && (
        <Campo
          label="Contratto"
          value={`${riga.contratto.numero} (${formatDate(riga.contratto.dataInizio)} → ${formatDate(riga.contratto.dataFine)})`}
        />
      )}
    </div>
  </div>
);

const DrillDownCapitolato: React.FC<{ riga: RigaOrdine }> = ({ riga }) => {
  if (riga.tipoRigaCapitolato === 'NON_CAPITOLATO') return null;
  return (
    <div className="bop-scheda-ordine__drilldown-block">
      <h4 className="bop-scheda-ordine__drilldown-title">Capitolato</h4>
      <div className="bop-scheda-ordine__griglia">
        <Campo label="Tipo riga capitolato" value={riga.tipoRigaCapitolato} />
        <Campo label="Scorporo capitolato" value={formatCurrency(nz(riga.scorporoCapitolato))} />
        <Campo
          label="Delta calcolato"
          value={
            <span title="delta = importoNetto - scorporoCapitolato">
              {formatCurrency(nz(riga.deltaCalcolato))}
            </span>
          }
        />
        <Campo
          label="Extra acquirente calcolato"
          value={
            <span title="Quota extra a carico acquirente (formula T7)">
              {formatCurrency(nz(riga.extraAcquirenteCalcolato))}
            </span>
          }
        />
        <Campo
          label="Importo impresa calcolato"
          value={formatCurrency(nz(riga.importoImpresaCalcolato))}
        />
      </div>
    </div>
  );
};

// ── Tabella righe gerarchica ────────────────────────────────────────────────

const COL_COUNT = 11; // numero colonne header tabella righe

interface TabellaRigheGerarchicaProps {
  raggruppamenti: Raggruppamento[];
  righe: RigaOrdine[];
  expandedRighe: Set<number>;
  onToggleRiga: (rigaId: number) => void;
  canEsplodi: boolean;
  esplosioneBloccata: boolean;
  motivoBloccoEsplosione: string | null;
  onEsplodi: (riga: RigaOrdine) => void;
  canConfigura: boolean;
  configurazioneBloccata: boolean;
  motivoBloccoConfigurazione: string | null;
  onConfigura: (riga: RigaOrdine) => void;
  canContrattualizza: boolean;
  contrattualizzazioneBloccata: boolean;
  motivoBloccoContrattualizzazione: string | null;
  onContrattualizza: (riga: RigaOrdine) => void;
  onAnnullaContrattualizzazione: (riga: RigaOrdine) => void;
  onVediContratto: (contrattoId: number) => void;
}

const TabellaRigheGerarchica: React.FC<TabellaRigheGerarchicaProps> = ({
  raggruppamenti,
  righe,
  expandedRighe,
  onToggleRiga,
  canEsplodi,
  esplosioneBloccata,
  motivoBloccoEsplosione,
  onEsplodi,
  canConfigura,
  configurazioneBloccata,
  motivoBloccoConfigurazione,
  onConfigura,
  canContrattualizza,
  contrattualizzazioneBloccata,
  motivoBloccoContrattualizzazione,
  onContrattualizza,
  onAnnullaContrattualizzazione,
  onVediContratto,
}) => {
  // Indicizza raggruppamenti per parentId
  const figliPerParent = useMemo(() => {
    const m = new Map<number | null, Raggruppamento[]>();
    raggruppamenti.forEach((r) => {
      const k = r.parentId ?? null;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    });
    m.forEach((list) => list.sort((a, b) => a.ordinePos - b.ordinePos));
    return m;
  }, [raggruppamenti]);

  // Indicizza righe per raggruppamentoId
  const righePerRagg = useMemo(() => {
    const m = new Map<number | null, RigaOrdine[]>();
    righe.forEach((r) => {
      const k = r.raggruppamentoId ?? null;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    });
    m.forEach((list) => list.sort((a, b) => a.ordinePos - b.ordinePos));
    return m;
  }, [righe]);

  const totaleRighe = righe.length;

  if (totaleRighe === 0 && raggruppamenti.length === 0) {
    return <div className="bop-empty-state">Nessuna riga sull'ordine.</div>;
  }

  // Render ricorsivo header raggruppamento + figli + righe
  const renderRagg = (r: Raggruppamento): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    const cls =
      r.livello === 1
        ? 'bop-scheda-ordine__ragg bop-scheda-ordine__ragg--l1'
        : r.livello === 2
          ? 'bop-scheda-ordine__ragg bop-scheda-ordine__ragg--l2'
          : 'bop-scheda-ordine__ragg bop-scheda-ordine__ragg--l3';
    out.push(
      <tr key={`ragg-${r.id}`} className={cls}>
        <td colSpan={COL_COUNT}>
          <span className="bop-scheda-ordine__ragg-livello">L{r.livello}</span>
          <span className="bop-scheda-ordine__ragg-etichetta">{r.etichetta}</span>
          {r.descrizione ? <span className="bop-scheda-ordine__ragg-desc"> — {r.descrizione}</span> : null}
        </td>
      </tr>
    );
    // righe attached direttamente a questo raggruppamento
    const figliRighe = righePerRagg.get(r.id) ?? [];
    figliRighe.forEach((riga) => out.push(...renderRiga(riga)));
    // raggruppamenti figli
    const figliRagg = figliPerParent.get(r.id) ?? [];
    figliRagg.forEach((figlio) => out.push(...renderRagg(figlio)));
    return out;
  };

  const renderRiga = (riga: RigaOrdine): React.ReactNode[] => {
    const isExpandable =
      riga.tipoRiga === 'ARTICOLO_CONFIGURATO' ||
      riga.tipoRiga === 'RICORRENTE' ||
      riga.tipoRiga === 'A_CORPO' ||
      riga.tipoRigaCapitolato !== 'NON_CAPITOLATO';
    const expanded = expandedRighe.has(riga.id);

    const trClasses = ['bop-scheda-ordine__riga'];
    if (riga.daEsplodere) trClasses.push('bop-scheda-ordine__riga--da-esplodere');
    if (riga.daConfigurare && !riga.configurataAt) {
      trClasses.push('bop-scheda-ordine__riga--da-configurare');
    }
    if (riga.alternativaGruppo && !riga.alternativaScelta) {
      trClasses.push('bop-scheda-ordine__riga--alternativa-non-scelta');
    }

    const aliquota = riga.codiceIva?.aliquota ? `${formatNumber(Number(riga.codiceIva.aliquota))} %` : '—';

    const out: React.ReactNode[] = [
      <tr key={`riga-${riga.id}`} className={trClasses.join(' ')}>
        <td>
          {isExpandable ? (
            <button
              type="button"
              className="bop-btn-icon bop-scheda-ordine__expand-btn"
              onClick={() => onToggleRiga(riga.id)}
              aria-label={expanded ? 'Chiudi dettaglio' : 'Apri dettaglio'}
              title={expanded ? 'Chiudi dettaglio' : 'Apri dettaglio'}
            >
              {expanded ? '−' : '+'}
            </button>
          ) : null}
        </td>
        <td>{riga.ordinePos}</td>
        <td>
          <span className="bop-badge bop-badge--neutral">{ETICHETTE_TIPO_RIGA[riga.tipoRiga]}</span>
          {riga.daEsplodere && !riga.esplosaAt && (
            <span
              className="bop-badge bop-badge--warning bop-scheda-ordine__badge-da-esplodere"
              title="Riga da esplodere — validazione tecnica richiesta"
            >
              ⚠ Da esplodere
            </span>
          )}
          {riga.daEsplodere && riga.esplosaAt && (
            <span
              className="bop-badge bop-badge--success bop-scheda-ordine__badge-esplosa"
              title="Riga validata tecnicamente (esplosa)"
            >
              Esplosa
            </span>
          )}
          {riga.tipoRiga === 'A_CORPO' && riga.daEsplodere && canEsplodi && (
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm bop-scheda-ordine__btn-esplodi"
              onClick={() => onEsplodi(riga)}
              disabled={esplosioneBloccata}
              title={
                esplosioneBloccata
                  ? motivoBloccoEsplosione ?? 'Esplosione non disponibile'
                  : riga.esplosaAt
                    ? 'Modifica esplosione'
                    : 'Esplodi riga'
              }
            >
              {riga.esplosaAt ? 'Modifica esplosione' : 'Esplodi'}
            </button>
          )}
          {riga.daConfigurare && !riga.configurataAt && (
            <span
              className="bop-badge bop-badge--warning bop-scheda-ordine__badge-da-configurare"
              title="Riga da configurare — composizione differita in ordine"
            >
              ⚠ Da configurare
            </span>
          )}
          {riga.daConfigurare && riga.configurataAt && (
            <span
              className="bop-badge bop-badge--success bop-scheda-ordine__badge-configurata"
              title="Riga configurata"
            >
              Configurata
            </span>
          )}
          {riga.tipoRiga === 'ARTICOLO_CONFIGURATO' && riga.daConfigurare && canConfigura && (
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm bop-scheda-ordine__btn-configura"
              onClick={() => onConfigura(riga)}
              disabled={configurazioneBloccata}
              title={
                configurazioneBloccata
                  ? motivoBloccoConfigurazione ?? 'Configurazione non disponibile'
                  : riga.configurataAt
                    ? 'Modifica configurazione'
                    : 'Configura riga'
              }
            >
              {riga.configurataAt ? 'Modifica configurazione' : 'Configura'}
            </button>
          )}
          {riga.tipoRiga === 'RICORRENTE' && riga.daContrattualizzare && !riga.contratto && (
            <span
              className="bop-badge bop-badge--warning bop-scheda-ordine__badge-da-contrattualizzare"
              title="Riga ricorrente da contrattualizzare"
            >
              ⚠ Da contrattualizzare
            </span>
          )}
          {riga.tipoRiga === 'RICORRENTE' && riga.daContrattualizzare && !riga.contratto && canContrattualizza && (
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm bop-scheda-ordine__btn-contrattualizza"
              onClick={() => onContrattualizza(riga)}
              disabled={contrattualizzazioneBloccata}
              title={
                contrattualizzazioneBloccata
                  ? motivoBloccoContrattualizzazione ?? 'Contrattualizzazione non disponibile'
                  : 'Crea contratto dalla riga'
              }
            >
              Contrattualizza
            </button>
          )}
          {riga.tipoRiga === 'RICORRENTE' && riga.contratto && (
            <span
              className="bop-badge bop-badge--success bop-scheda-ordine__badge-contrattualizzata"
              title={`Contratto ${riga.contratto.numero}`}
            >
              Contrattualizzata
            </span>
          )}
          {riga.tipoRiga === 'RICORRENTE' && riga.contratto && (
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm bop-scheda-ordine__btn-vedi-contratto"
              onClick={() => onVediContratto(riga.contratto!.id)}
              title={`Contratto ${riga.contratto.numero}`}
            >
              Vedi contratto
            </button>
          )}
          {riga.tipoRiga === 'RICORRENTE' && riga.contratto && canContrattualizza && (
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm bop-scheda-ordine__btn-annulla-contrattualizzazione"
              onClick={() => onAnnullaContrattualizzazione(riga)}
              disabled={contrattualizzazioneBloccata}
              title={
                contrattualizzazioneBloccata
                  ? motivoBloccoContrattualizzazione ?? 'Contrattualizzazione non disponibile'
                  : 'Annulla contrattualizzazione'
              }
            >
              Annulla contrattualizzazione
            </button>
          )}
          {riga.alternativaGruppo && (
            <span
              className="bop-badge bop-badge--info"
              title={`Alternativa (${riga.alternativaGruppo})${riga.etichettaAlternativa ? ` — ${riga.etichettaAlternativa}` : ''}`}
            >
              Alt{riga.alternativaScelta ? ' ✓' : ''}
            </span>
          )}
          {riga.tipoRigaCapitolato !== 'NON_CAPITOLATO' && (
            <span className="bop-badge bop-badge--primary" title={`Capitolato: ${riga.tipoRigaCapitolato}`}>
              Cap
            </span>
          )}
        </td>
        <td>{riga.articolo?.codice ?? riga.codicePrecopy ?? '—'}</td>
        <td>
          <div className="bop-scheda-ordine__riga-descrizione">{riga.descrizione}</div>
          {riga.descrizioneEstesa && (
            <div className="bop-scheda-ordine__riga-descrizione-estesa">{riga.descrizioneEstesa}</div>
          )}
        </td>
        <td>{riga.umVendita ?? '—'}</td>
        <td className="bop-text-right">{formatQuantity(nz(riga.quantita))}</td>
        <td className="bop-text-right">{formatCurrency(nz(riga.prezzoUnitario))}</td>
        <td className="bop-text-right">{riga.sconto ? `${formatNumber(nz(riga.sconto))} %` : '—'}</td>
        <td className="bop-text-right">{formatCurrency(nz(riga.importoNetto))}</td>
        <td>{riga.codiceIva?.codice ?? '—'}<span className="bop-text-muted"> ({aliquota})</span></td>
      </tr>,
    ];

    if (expanded && isExpandable) {
      out.push(
        <tr key={`riga-${riga.id}-drill`} className="bop-scheda-ordine__riga-drill">
          <td colSpan={COL_COUNT}>
            <div className="bop-scheda-ordine__drilldown">
              {(riga.tipoRiga === 'ARTICOLO_CONFIGURATO' || riga.tipoRiga === 'A_CORPO') && (
                <DrillDownComponenti componenti={riga.componenti} />
              )}
              {riga.tipoRiga === 'RICORRENTE' && <DrillDownRicorrenza riga={riga} />}
              <DrillDownCapitolato riga={riga} />
            </div>
          </td>
        </tr>
      );
    }
    return out;
  };

  const rows: React.ReactNode[] = [];
  // 1) righe senza raggruppamento (root)
  (righePerRagg.get(null) ?? []).forEach((r) => rows.push(...renderRiga(r)));
  // 2) raggruppamenti L1 (parent=null) e relative cascate
  (figliPerParent.get(null) ?? []).forEach((r) => rows.push(...renderRagg(r)));

  return (
    <div className="bop-scheda-ordine__tabella-wrapper">
      <table className="bop-table bop-scheda-ordine__tabella-righe">
        <thead>
          <tr>
            <th className="bop-scheda-ordine__col-expand"> </th>
            <th>#</th>
            <th>Tipo</th>
            <th>Codice</th>
            <th>Descrizione</th>
            <th>U.M.</th>
            <th className="bop-text-right">Q.tà</th>
            <th className="bop-text-right">Prezzo</th>
            <th className="bop-text-right">Sconto</th>
            <th className="bop-text-right">Importo netto</th>
            <th>IVA</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
};

// ── Lista Allegati ──────────────────────────────────────────────────────────

interface ListaAllegatiProps {
  ordineId: number;
  allegati: AllegatoOrdine[];
  canEdit: boolean;
  onChanged: () => void;
}
const ListaAllegati: React.FC<ListaAllegatiProps> = ({ ordineId, allegati, canEdit, onChanged }) => {
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadNome, setUploadNome] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const [uploadVisibile, setUploadVisibile] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!uploadFile) {
      showToast('Seleziona un file', 'warning');
      return;
    }
    if (!uploadNome.trim()) {
      showToast('Inserisci un nome', 'warning');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('nome', uploadNome.trim());
      if (uploadNote.trim()) fd.append('note', uploadNote.trim());
      fd.append('visibileAcquirente', String(uploadVisibile));
      // @api POST /api/ordini/:id/allegati {"file":"File","nome":"string","note":"string?","visibileAcquirente":"boolean?"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch<{ success: boolean; data: { id: number } }>(`/api/ordini/${ordineId}/allegati`, {
        method: 'POST',
        body: fd,
      });
      showToast('Allegato caricato', 'success');
      setShowUpload(false);
      setUploadFile(null);
      setUploadNome('');
      setUploadNote('');
      setUploadVisibile(false);
      onChanged();
    } catch (e) {
      const err = e as Error;
      showToast(err.message || 'Errore upload', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (a: AllegatoOrdine) => {
    if (!window.confirm(`Eliminare l'allegato "${a.nome}"?`)) return;
    try {
      // @api DELETE /api/ordini/:id/allegati/:allegatoId -> {"success":true}
      await apiFetch(`/api/ordini/${ordineId}/allegati/${a.id}`, { method: 'DELETE' });
      showToast('Allegato eliminato', 'success');
      onChanged();
    } catch (e) {
      const err = e as Error;
      showToast(err.message || 'Errore eliminazione', 'error');
    }
  };

  return (
    <div className="bop-scheda-ordine__allegati">
      {canEdit && !showUpload && (
        <button type="button" className="bop-btn-primary bop-btn-sm" onClick={() => setShowUpload(true)}>
          + Carica allegato
        </button>
      )}

      {showUpload && (
        <div className="bop-card bop-scheda-ordine__upload-card">
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">File</label>
              <input
                type="file"
                className="bop-input"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                disabled={uploading}
              />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Nome</label>
              <input
                type="text"
                className="bop-input"
                value={uploadNome}
                onChange={(e) => setUploadNome(e.target.value)}
                disabled={uploading}
                placeholder="Es. Capitolato tecnico"
              />
            </div>
          </div>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Note</label>
              <input
                type="text"
                className="bop-input"
                value={uploadNote}
                onChange={(e) => setUploadNote(e.target.value)}
                disabled={uploading}
              />
            </div>
            <div className="bop-form-group bop-scheda-ordine__form-group-check">
              <label className="bop-label">
                <input
                  type="checkbox"
                  checked={uploadVisibile}
                  onChange={(e) => setUploadVisibile(e.target.checked)}
                  disabled={uploading}
                />
                {' '}Visibile acquirente
              </label>
            </div>
          </div>
          <div className="bop-flex">
            <button type="button" className="bop-btn-primary bop-btn-sm" onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Caricamento…' : 'Carica'}
            </button>
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm"
              onClick={() => setShowUpload(false)}
              disabled={uploading}
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {allegati.length === 0 ? (
        <div className="bop-empty-state">Nessun allegato.</div>
      ) : (
        <table className="bop-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>File</th>
              <th className="bop-text-right">Dimensione</th>
              <th>Note</th>
              <th>Visibile acq.</th>
              <th>Caricato da</th>
              <th>Quando</th>
              <th className="bop-text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {allegati.map((a) => (
              <tr key={a.id}>
                <td>{a.nome}</td>
                <td>{a.nomeFile}</td>
                <td className="bop-text-right">{formatBytes(a.dimensione)}</td>
                <td>{a.note ?? '—'}</td>
                <td>
                  {a.visibileAcquirente ? (
                    <span className="bop-badge bop-badge--info">Sì</span>
                  ) : (
                    <span className="bop-badge bop-badge--neutral">No</span>
                  )}
                </td>
                <td>{a.uploadedBy?.nome ?? a.uploadedBy?.email ?? '—'}</td>
                <td>{formatDateTime(a.uploadedAt)}</td>
                <td className="bop-text-right">
                  {/* @api GET /api/ordini/:id/allegati/:allegatoId/download -> file stream (attachment) */}
                  <button
                    type="button"
                    className="bop-btn-secondary bop-btn-sm"
                    onClick={() => apiDownload(`/api/ordini/${ordineId}/allegati/${a.id}/download`, a.nomeFile || 'allegato')}
                  >
                    Scarica
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      className="bop-btn-danger bop-btn-sm bop-scheda-ordine__btn-elimina"
                      onClick={() => handleDelete(a)}
                    >
                      Elimina
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Tabella Log ─────────────────────────────────────────────────────────────

const TabellaLog: React.FC<{ log: OrdineLogEntry[] }> = ({ log }) => {
  if (!log || log.length === 0) {
    return <div className="bop-empty-state">Nessuna voce di log.</div>;
  }
  const ordinato = [...log].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  return (
    <table className="bop-table">
      <thead>
        <tr>
          <th>Data/ora</th>
          <th>Utente</th>
          <th>Azione</th>
          <th>Dettaglio</th>
        </tr>
      </thead>
      <tbody>
        {ordinato.map((l) => (
          <tr key={l.id}>
            <td>{formatDateTime(l.createdAt)}</td>
            <td>{l.utenteName}</td>
            <td>{l.azione}</td>
            <td>{l.dettaglio ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── Sezione Distinte (solo flusso APPALTO_CONTRATTO, ordine non in bozza) ───

type TipoDistinta = 'MERCE' | 'LAVORAZIONI';
type AddebitoDistinta = 'NOSTRO' | 'CLIENTE';
type StatoDistinta = 'BOZZA' | 'CONFERMATA';

interface DistintaOrdine {
  id: number;
  numero: number;
  tipo: TipoDistinta;
  addebito: AddebitoDistinta;
  descrizione: string | null;
  stato: StatoDistinta;
  confermataAt: string | null;
  _count: { righe: number };
}

const ETICHETTE_TIPO_DISTINTA: Record<TipoDistinta, string> = {
  MERCE: 'Merce',
  LAVORAZIONI: 'Lavorazioni',
};

const ETICHETTE_ADDEBITO_DISTINTA: Record<AddebitoDistinta, string> = {
  NOSTRO: 'Nostro',
  CLIENTE: 'Cliente',
};

const BadgeStatoDistinta: React.FC<{ stato: StatoDistinta }> = ({ stato }) =>
  stato === 'CONFERMATA' ? (
    <span className="bop-badge bop-badge--success">Confermata</span>
  ) : (
    <span className="bop-badge bop-badge--neutral">Bozza</span>
  );

const SezioneDistinte: React.FC<{ ordine: OrdineDettaglio }> = ({ ordine }) => {
  const navigate = useNavigate();
  const { can } = usePermesso();
  const [distinte, setDistinte] = useState<DistintaOrdine[]>([]);
  const [loadingDistinte, setLoadingDistinte] = useState(true);
  const [modalTipo, setModalTipo] = useState<TipoDistinta | null>(null);

  const caricaDistinte = useCallback(async () => {
    setLoadingDistinte(true);
    try {
      // @api GET /api/distinte?societaId&ordineId -> {"success":true,"data":[{"id":"number","numero":"number","tipo":"MERCE|LAVORAZIONI","addebito":"NOSTRO|CLIENTE","descrizione":"string|null","stato":"BOZZA|CONFERMATA","confermataAt":"string|null","_count":{"righe":"number"}}]}
      const res = await apiFetch<{ success: boolean; data?: DistintaOrdine[]; error?: string }>(
        `/api/distinte?societaId=${ordine.societaId}&ordineId=${ordine.id}`
      );
      if (!res.success) {
        showToast(res.error || 'Errore nel caricamento delle distinte', 'error');
        setDistinte([]);
      } else {
        setDistinte(res.data ?? []);
      }
    } catch (e) {
      const err = e as Error;
      showToast(err.message || 'Errore nel caricamento delle distinte', 'error');
      setDistinte([]);
    } finally {
      setLoadingDistinte(false);
    }
  }, [ordine.societaId, ordine.id]);

  useEffect(() => {
    caricaDistinte();
  }, [caricaDistinte]);

  const creaDistinta = async (righe: RigaEstratta[]) => {
    if (!modalTipo) return;
    try {
      // @api POST /api/distinte {"ordineId":"number","tipo":"MERCE|LAVORAZIONI","righe":[{"rigaOrdineId":"number|null","componenteId":"number|null","articoloId":"number|null","descrizione":"string","quantita":"number","unitaMisura":"string|null","note":"string|null"}]} -> {"success":true,"data":{"id":"number","numero":"number"}}
      const res = await apiFetch<{
        success: boolean;
        data?: { id: number; numero: number };
        error?: string | { code?: string; message?: string };
      }>('/api/distinte', {
        method: 'POST',
        body: JSON.stringify({ ordineId: ordine.id, tipo: modalTipo, righe }),
      });
      if (!res.success) {
        const msg = typeof res.error === 'string' ? res.error : res.error?.message;
        showToast(msg || 'Errore nella creazione della distinta', 'error');
        return;
      }
      showToast('Distinta creata', 'success');
      setModalTipo(null);
      await caricaDistinte();
    } catch (e) {
      // apiFetch allega il body della risposta in err.payload (es. 409 FLUSSO_NON_AMMESSO / ORDINE_NON_CONFERMATO)
      const err = e as Error & { payload?: { error?: string | { code?: string; message?: string } } };
      const payloadErr = err.payload?.error;
      const msg = typeof payloadErr === 'string' ? payloadErr : payloadErr?.message;
      showToast(msg || err.message || 'Errore nella creazione della distinta', 'error');
    }
  };

  const canEditDistinte = can('commerciale.distinte', 'edit');

  return (
    <section className="bop-card bop-scheda-ordine__section">
      <div className="bop-card__header">
        <h2 className="bop-card__title">Distinte ({distinte.length})</h2>
        {canEditDistinte && (
          <div className="bop-scheda-ordine__distinte-actions">
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm"
              onClick={() => setModalTipo('MERCE')}
            >
              + Distinta merce
            </button>
            <button
              type="button"
              className="bop-btn-secondary bop-btn-sm"
              onClick={() => setModalTipo('LAVORAZIONI')}
            >
              + Distinta lavorazioni
            </button>
          </div>
        )}
      </div>
      <div className="bop-card__body">
        {loadingDistinte ? (
          <div className="bop-empty-state">Caricamento distinte…</div>
        ) : distinte.length === 0 ? (
          <div className="bop-empty-state">
            Nessuna distinta. Estrai le righe merce/lavorazioni dai componenti dell'ordine.
          </div>
        ) : (
          <table className="bop-table bop-table--clickable">
            <thead>
              <tr>
                <th>Distinta</th>
                <th>Tipo</th>
                <th>Addebito</th>
                <th className="bop-text-right">N. righe</th>
                <th>Stato</th>
                <th>Confermata il</th>
              </tr>
            </thead>
            <tbody>
              {distinte.map((d) => (
                <tr key={d.id} onClick={() => navigate(`/commerciale/attivo/distinte/${d.id}`)}>
                  <td>{`${ordine.numero}-D${d.numero}`}</td>
                  <td>{ETICHETTE_TIPO_DISTINTA[d.tipo]}</td>
                  <td>{ETICHETTE_ADDEBITO_DISTINTA[d.addebito]}</td>
                  <td className="bop-text-right">{d._count?.righe ?? 0}</td>
                  <td><BadgeStatoDistinta stato={d.stato} /></td>
                  <td>{d.confermataAt ? formatDateTime(d.confermataAt) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalTipo && (
        <ModalEstraiRighe
          ordineId={ordine.id}
          tipo={modalTipo}
          onClose={() => setModalTipo(null)}
          onConfirm={creaDistinta}
        />
      )}
    </section>
  );
};

// ── Sezione Evasione (DDT e SAL generati dall'ordine) ───────────────────────

type TipoDdt = 'VENDITA' | 'CANTIERE';
type StatoDdt = 'BOZZA' | 'EMESSO' | 'ANNULLATO';
type StatoSal = 'BOZZA' | 'EMESSO' | 'APPROVATO_CLIENTE' | 'APPROVATO_BACKOFFICE' | 'FATTURATO';

interface DdtEvasione {
  id: number;
  tipo: TipoDdt;
  stato: StatoDdt;
  numero: string | null;
  dataEmissione: string | null;
  _count: { righe: number };
}

interface SalEvasione {
  id: number;
  numero: string | null;
  stato: StatoSal;
  dataEmissione: string | null;
  importoTotale: number | string | null;
}

const BADGE_TIPO_DDT: Record<TipoDdt, { label: string; cls: string }> = {
  VENDITA: { label: 'Vendita', cls: 'bop-badge--info' },
  CANTIERE: { label: 'Cantiere', cls: 'bop-badge--warning' },
};

const BADGE_STATO_DDT: Record<StatoDdt, { label: string; cls: string }> = {
  BOZZA: { label: 'Bozza', cls: 'bop-badge--neutral' },
  EMESSO: { label: 'Emesso', cls: 'bop-badge--info' },
  ANNULLATO: { label: 'Annullato', cls: 'bop-badge--danger' },
};

const BADGE_STATO_SAL: Record<StatoSal, { label: string; cls: string }> = {
  BOZZA: { label: 'Bozza', cls: 'bop-badge--neutral' },
  EMESSO: { label: 'Emesso', cls: 'bop-badge--info' },
  APPROVATO_CLIENTE: { label: 'App. cliente', cls: 'bop-badge--success' },
  APPROVATO_BACKOFFICE: { label: 'App. backoffice', cls: 'bop-badge--success' },
  FATTURATO: { label: 'Fatturato', cls: 'bop-badge--success' },
};

const SezioneEvasione: React.FC<{ ordine: OrdineDettaglio }> = ({ ordine }) => {
  const navigate = useNavigate();
  const { can } = usePermesso();
  const canDdt = can('commerciale.ddt', 'view');
  const canSal = can('commerciale.sal', 'view') && ordine.flussoCommerciale === 'APPALTO_CONTRATTO';

  const [ddt, setDdt] = useState<DdtEvasione[]>([]);
  const [sal, setSal] = useState<SalEvasione[]>([]);
  const [loadingEvasione, setLoadingEvasione] = useState(true);

  useEffect(() => {
    let attivo = true;
    const caricaDdt = async (): Promise<DdtEvasione[]> => {
      if (!canDdt) return [];
      try {
        // @api GET /api/ddt?societaId&ordineId -> {"success":true,"data":[{"id":"number","tipo":"VENDITA|CANTIERE","stato":"BOZZA|EMESSO|ANNULLATO","numero":"string|null","dataEmissione":"string|null","dataTrasporto":"string|null","clienteRagioneSociale":"string|null","_count":{"righe":"number"}}]}
        const res = await apiFetch<{ success: boolean; data?: DdtEvasione[]; error?: unknown }>(
          `/api/ddt?societaId=${ordine.societaId}&ordineId=${ordine.id}`
        );
        return res.success ? res.data ?? [] : [];
      } catch (e) {
        if (attivo) showToast(estraiErrore(e, 'Errore nel caricamento dei DDT'), 'error');
        return [];
      }
    };
    const caricaSal = async (): Promise<SalEvasione[]> => {
      if (!canSal) return [];
      try {
        // @api GET /api/sal?societaId&ordineId -> {"success":true,"data":[{"id":"number","numero":"string|null","stato":"BOZZA|EMESSO|APPROVATO_CLIENTE|APPROVATO_BACKOFFICE|FATTURATO","dataEmissione":"string|null","importoTotale":"number","_count":{"righe":"number"}}]}
        const res = await apiFetch<{ success: boolean; data?: SalEvasione[]; error?: unknown }>(
          `/api/sal?societaId=${ordine.societaId}&ordineId=${ordine.id}`
        );
        return res.success ? res.data ?? [] : [];
      } catch (e) {
        if (attivo) showToast(estraiErrore(e, 'Errore nel caricamento dei SAL'), 'error');
        return [];
      }
    };
    (async () => {
      setLoadingEvasione(true);
      const [listaDdt, listaSal] = await Promise.all([caricaDdt(), caricaSal()]);
      if (!attivo) return;
      setDdt(listaDdt);
      setSal(listaSal);
      setLoadingEvasione(false);
    })();
    return () => { attivo = false; };
  }, [canDdt, canSal, ordine.societaId, ordine.id]);

  return (
    <section className="bop-card bop-scheda-ordine__section">
      <div className="bop-card__header">
        <h2 className="bop-card__title">Evasione</h2>
      </div>
      <div className="bop-card__body">
        {loadingEvasione ? (
          <div className="bop-empty-state">Caricamento evasione…</div>
        ) : (
          <div
            className={
              canDdt && canSal
                ? 'bop-scheda-ordine__evasione-grid bop-scheda-ordine__evasione-grid--doppia'
                : 'bop-scheda-ordine__evasione-grid'
            }
          >
            {canDdt && (
              <div className="bop-scheda-ordine__evasione-blocco">
                <h3 className="bop-scheda-ordine__evasione-titolo">DDT ({ddt.length})</h3>
                {ddt.length === 0 ? (
                  <div className="bop-scheda-ordine__evasione-vuoto">Nessun DDT generato da questo ordine.</div>
                ) : (
                  <table className="bop-table bop-table--clickable">
                    <thead>
                      <tr>
                        <th>Numero</th>
                        <th>Tipo</th>
                        <th>Stato</th>
                        <th>Data</th>
                        <th className="bop-text-right">Righe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ddt.map((d) => {
                        const tipoCfg = BADGE_TIPO_DDT[d.tipo] ?? { label: d.tipo, cls: 'bop-badge--neutral' };
                        const statoCfg = BADGE_STATO_DDT[d.stato] ?? { label: d.stato, cls: 'bop-badge--neutral' };
                        return (
                          <tr key={d.id} onClick={() => navigate(`/commerciale/attivo/ddt/${d.id}`)}>
                            <td>{d.numero ?? <span className="bop-badge bop-badge--neutral">Bozza</span>}</td>
                            <td><span className={`bop-badge ${tipoCfg.cls}`}>{tipoCfg.label}</span></td>
                            <td><span className={`bop-badge ${statoCfg.cls}`}>{statoCfg.label}</span></td>
                            <td>{formatDate(d.dataEmissione)}</td>
                            <td className="bop-text-right">{d._count?.righe ?? 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            {canSal && (
              <div className="bop-scheda-ordine__evasione-blocco">
                <h3 className="bop-scheda-ordine__evasione-titolo">SAL ({sal.length})</h3>
                {sal.length === 0 ? (
                  <div className="bop-scheda-ordine__evasione-vuoto">Nessun SAL generato da questo ordine.</div>
                ) : (
                  <table className="bop-table bop-table--clickable">
                    <thead>
                      <tr>
                        <th>Numero</th>
                        <th>Stato</th>
                        <th className="bop-text-right">Importo</th>
                        <th>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sal.map((s) => {
                        const statoCfg = BADGE_STATO_SAL[s.stato] ?? { label: s.stato, cls: 'bop-badge--neutral' };
                        return (
                          <tr key={s.id} onClick={() => navigate(`/commerciale/attivo/sal/${s.id}`)}>
                            <td>{s.numero ?? <span className="bop-badge bop-badge--neutral">Bozza</span>}</td>
                            <td><span className={`bop-badge ${statoCfg.cls}`}>{statoCfg.label}</span></td>
                            <td className="bop-text-right">
                              {formatCurrency(s.importoTotale != null ? Number(s.importoTotale) : null)}
                            </td>
                            <td>{formatDate(s.dataEmissione)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// SchedaOrdine — pagina principale
// ══════════════════════════════════════════════════════════════════════════════

const SchedaOrdine: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ordineId = parseInt(id ?? '0', 10);

  const [ordine, setOrdine] = useState<OrdineDettaglio | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRighe, setExpandedRighe] = useState<Set<number>>(new Set());
  const [esplodiRiga, setEsplodiRiga] = useState<RigaOrdine | null>(null);
  const [configuraRiga, setConfiguraRiga] = useState<RigaOrdine | null>(null);
  const [contrattualizzaRiga, setContrattualizzaRiga] = useState<RigaOrdine | null>(null);
  const [modalGeneraDdt, setModalGeneraDdt] = useState(false);
  // Stampa / invio della conferma d'ordine: motore di stampa, modulo ORDINE_CLIENTE
  const [modalStampa, setModalStampa] = useState(false);
  const apriStampa = () => setModalStampa(true);

  useBloccoRecordContesto({
    recordSocietaId: ordine?.societaId,
    label: ordine ? `Ordine ${ordine.numero}` : 'Ordine',
  });

  const { can } = usePermesso();
  const [generandoAcconto, setGenerandoAcconto] = useState(false);
  const [confermando, setConfermando] = useState(false);
  const [riportandoInBozza, setRiportandoInBozza] = useState(false);
  const [eliminando, setEliminando] = useState(false);

  const reload = useCallback(async () => {
    if (!ordineId) return;
    setLoading(true);
    try {
      // @api GET /api/ordini/:id -> {"success":true,"data":{"ordine":"ricco","accontoPrevisto":"boolean","accontoTotaleFreeze":"string|null","proformeAcconto":"array"}}
      const res = await apiFetch<{ success: boolean; data: OrdineDettaglio; error?: string }>(
        `/api/ordini/${ordineId}`
      );
      if (!res.success) {
        setError(res.error || 'Errore caricamento ordine');
        setOrdine(null);
      } else {
        setOrdine(res.data);
        setError(null);
      }
    } catch (e) {
      const err = e as Error;
      setError(err.message || 'Errore');
      setOrdine(null);
    } finally {
      setLoading(false);
    }
  }, [ordineId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const toggleExpansion = (rigaId: number) => {
    setExpandedRighe((prev) => {
      const next = new Set(prev);
      if (next.has(rigaId)) next.delete(rigaId);
      else next.add(rigaId);
      return next;
    });
  };

  // ── Render guard ──
  if (loading) {
    return (
      <Layout>
        <div className="bop-page bop-scheda-ordine">
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento ordine…</div>
          </div>
          <ApiInspector docs={API_DOCS} />
        </div>
      </Layout>
    );
  }

  if (error === 'Accesso negato') {
    return (
      <Layout>
        <div className="bop-page bop-scheda-ordine">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Non hai i permessi</h3>
              <p className="bop-empty-state__text">Non puoi visualizzare questo ordine.</p>
              <BopBackLink to="/commerciale/attivo/ordini" label="Torna agli ordini" />
            </div>
          </div>
          <ApiInspector docs={API_DOCS} />
        </div>
      </Layout>
    );
  }

  if (!ordine) {
    return (
      <Layout>
        <div className="bop-page bop-scheda-ordine">
          <div className="bop-card">
            <div className="bop-empty-state">
              <h3 className="bop-empty-state__title">Ordine non trovato</h3>
              <p className="bop-empty-state__text">{error ?? 'L\'ordine richiesto non esiste o è stato eliminato.'}</p>
              <BopBackLink to="/commerciale/attivo/ordini" label="Torna agli ordini" />
            </div>
          </div>
          <ApiInspector docs={API_DOCS} />
        </div>
      </Layout>
    );
  }

  // ── Conteggi righe da esplodere (combo A_CORPO non ancora esplose) ──
  const righeDaEsplodere = ordine.righe.filter((r) => r.daEsplodere && !r.esplosaAt);
  const haRigheDaEsplodere = righeDaEsplodere.length > 0;
  const righeDaConfigurare = ordine.righe.filter((r) => r.daConfigurare && !r.configurataAt);
  const haRigheDaConfigurare = righeDaConfigurare.length > 0;
  const righeDaContrattualizzare = ordine.righe.filter(
    (r) => r.tipoRiga === 'RICORRENTE' && r.daContrattualizzare && !r.contratto
  );
  const haRigheDaContrattualizzare = righeDaContrattualizzare.length > 0;

  const logEntries: OrdineLogEntry[] = ordine.log ?? ordine.logs ?? [];

  const acquirenteLabel = ordine.acquirente
    ? [
        ordine.acquirente.unitaCodice,
        ordine.acquirente.unitaDescrizione,
        [ordine.acquirente.nome, ordine.acquirente.cognome].filter(Boolean).join(' ').trim() || null,
      ]
        .filter(Boolean)
        .join(' — ')
    : null;

  const creatoDa =
    ordine.createdBy?.nome ?? ordine.createdBy?.email ?? '—';

  // ── D17 — Box acconto (priorità D → C → B → A) ──────────────────────────
  // Proforma "viva da conversione" = origine DA_CONVERSIONE_ORDINE e stato != ANNULLATA
  const proformeVive = (ordine.proformeAcconto ?? []).filter(
    (p) => p.origine === 'DA_CONVERSIONE_ORDINE' && p.stato !== 'ANNULLATA'
  );
  const proformaPagata = proformeVive.find((p) => p.stato === 'PAGATA') ?? null;
  const proformaDaIncassare = proformeVive.find((p) => p.stato === 'DA_INCASSARE') ?? null;

  let accontoBoxStato: 'A' | 'B' | 'C' | 'D' = 'A';
  if (proformaPagata) accontoBoxStato = 'D';
  else if (proformaDaIncassare) accontoBoxStato = 'C';
  else if (ordine.accontoPrevisto) accontoBoxStato = 'B';

  const generaProformaAcconto = async () => {
    if (generandoAcconto) return;
    setGenerandoAcconto(true);
    try {
      // @api POST /api/proforme-acconto/rigenera-da-ordine/:ordineId -> {"success":true,"data":{"proformaId":"number","numero":"string"}}
      const res = await apiFetch<{ success: boolean; data?: { proformaId: number; numero: string }; error?: string }>(
        `/api/proforme-acconto/rigenera-da-ordine/${ordine.id}`,
        { method: 'POST' }
      );
      if (!res.success) {
        showToast(res.error || 'Errore generazione proforma acconto', 'error');
      } else {
        showToast(`Proforma acconto ${res.data?.numero ?? ''} generata`, 'success');
        await reload();
      }
    } catch (e) {
      const err = e as Error;
      showToast(err.message || 'Errore generazione proforma acconto', 'error');
    } finally {
      setGenerandoAcconto(false);
    }
  };

  // ── Conferma ordine (BOZZA → CONFERMATO) ────────────────────────────────
  // Stessi blocchi del backend: righe da lavorare + acconto da conversione non pagato.
  const confermaBloccataMotivo = haRigheDaEsplodere
    ? 'Ci sono righe a corpo da esplodere: conferma bloccata'
    : haRigheDaConfigurare
    ? 'Ci sono righe configurate da completare: conferma bloccata'
    : haRigheDaContrattualizzare
    ? 'Ci sono righe ricorrenti da contrattualizzare: conferma bloccata'
    : proformaDaIncassare
    ? 'Acconto da conversione non ancora pagato: conferma bloccata'
    : null;

  const confermaOrdine = async () => {
    if (confermando || confermaBloccataMotivo) return;
    if (!window.confirm(`Confermare l'ordine ${ordine.numero}? Le righe non saranno più modificabili.`)) return;
    setConfermando(true);
    try {
      // @api POST /api/ordini/:id/conferma -> {"success":true,"data":{"stato":"CONFERMATO","dataConferma":"string","modalitaEvasione":"DDT|MISTA"}}
      const res = await apiFetch<{ success: boolean; data?: { stato: string }; error?: any }>(
        `/api/ordini/${ordine.id}/conferma`,
        { method: 'POST' }
      );
      if (!res.success) {
        const msg = typeof res.error === 'string' ? res.error : res.error?.message;
        showToast(msg || 'Errore conferma ordine', 'error');
      } else {
        showToast(`Ordine ${ordine.numero} confermato`, 'success');
      }
      await reload();
    } catch (e) {
      const err = e as Error;
      showToast(err.message || 'Errore conferma ordine', 'error');
    } finally {
      setConfermando(false);
    }
  };

  const riportaInBozza = async () => {
    if (riportandoInBozza) return;
    if (!window.confirm(`Riportare in bozza l'ordine ${ordine.numero}?`)) return;
    setRiportandoInBozza(true);
    try {
      // @api POST /api/ordini/:id/riporta-in-bozza -> {"success":true,"data":{"stato":"BOZZA"}}
      const res = await apiFetch<{ success: boolean; data?: { stato: string }; error?: any }>(
        `/api/ordini/${ordine.id}/riporta-in-bozza`,
        { method: 'POST' }
      );
      if (!res.success) {
        const msg = typeof res.error === 'string' ? res.error : res.error?.message;
        showToast(msg || 'Errore riporto in bozza', 'error');
      } else {
        showToast(`Ordine ${ordine.numero} riportato in bozza`, 'success');
      }
      await reload();
    } catch (e) {
      const err = e as Error;
      showToast(err.message || 'Errore riporto in bozza', 'error');
    } finally {
      setRiportandoInBozza(false);
    }
  };

  // ── Elimina ordine (ciclo attivo a ritroso) ─────────────────────────────
  // Il backend rifiuta (409) se esistono documenti a valle; se l'ordine nasce
  // da un preventivo e non ne restano altri, il preventivo torna ACCETTATO.
  const eliminaOrdine = async () => {
    if (eliminando) return;
    const avviso = ordine.preventivoOrigineId
      ? `Eliminare definitivamente l'ordine ${ordine.numero}?\nIl preventivo di origine tornerà in stato ACCETTATO.`
      : `Eliminare definitivamente l'ordine ${ordine.numero}?`;
    if (!window.confirm(avviso)) return;
    setEliminando(true);
    try {
      // @api DELETE /api/ordini/:id -> {"success":true,"data":{"preventivoRipristinato":{"id":"number","numero":"string"}|null}}
      const res = await apiFetch<{ success: boolean; data?: { preventivoRipristinato: { id: number; numero: string } | null } }>(
        `/api/ordini/${ordine.id}`,
        { method: 'DELETE' }
      );
      const prev = res.data?.preventivoRipristinato;
      showToast(
        prev ? `Ordine ${ordine.numero} eliminato. Preventivo ${prev.numero} riportato ad ACCETTATO` : `Ordine ${ordine.numero} eliminato`,
        'success'
      );
      navigate('/commerciale/attivo/ordini');
    } catch (e) {
      const err = e as Error;
      showToast(err.message || "Errore nell'eliminazione dell'ordine", 'error');
      setEliminando(false);
    }
  };

  const annullaContrattualizzazione = async (riga: RigaOrdine) => {
    if (!window.confirm(`Annullare la contrattualizzazione della riga ${riga.ordinePos}?`)) return;
    try {
      // @api DELETE /api/ordini/:id/righe/:rigaId/contrattualizzazione -> {"success":true}
      const res = await apiFetch<{ success: boolean; error?: any }>(
        `/api/ordini/${ordine.id}/righe/${riga.id}/contrattualizzazione`,
        { method: 'DELETE' }
      );
      if (!res.success) {
        showToast(res.error?.message || 'Errore annullamento contrattualizzazione', 'error');
      } else {
        showToast('Contrattualizzazione annullata', 'success');
        await reload();
      }
    } catch (e) {
      const err = e as Error;
      showToast(err.message || 'Errore annullamento contrattualizzazione', 'error');
    }
  };

  return (
    <Layout>
      <div className="bop-page bop-scheda-ordine">
        {/* Header */}
        <BopBackLink to="/commerciale/attivo/ordini" label="Torna agli ordini" />
        <header className="bop-scheda-ordine__header">
          <div className="bop-scheda-ordine__header-top">
            <div className="bop-scheda-ordine__breadcrumb">
              Commerciale ›{' '}
              <Link to="/commerciale/attivo/ordini">Ordini Clienti</Link> › {ordine.numero}
            </div>
            <div className="bop-scheda-ordine__header-actions">
              <button
                type="button"
                className="bop-btn-secondary"
                onClick={apriStampa}
                title="Anteprima, PDF o invio email della conferma d'ordine con il template di Impostazioni → Report"
              >
                Stampa / Invia
              </button>
              {ordine.stato === 'BOZZA' && can('commerciale.ordini_clienti', 'edit') && (
                <button
                  type="button"
                  className="bop-btn-primary"
                  disabled={confermando || !!confermaBloccataMotivo}
                  title={confermaBloccataMotivo ?? undefined}
                  onClick={confermaOrdine}
                >
                  {confermando ? 'Conferma…' : 'Conferma ordine'}
                </button>
              )}
              {ordine.stato === 'CONFERMATO' && can('commerciale.ordini_clienti', 'edit') && (
                <button
                  type="button"
                  className="bop-btn-secondary"
                  disabled={riportandoInBozza}
                  title="Disponibile finché non esistono documenti a valle (SAL/distinte/DDT)"
                  onClick={riportaInBozza}
                >
                  {riportandoInBozza ? 'Riporto…' : 'Riporta in bozza'}
                </button>
              )}
              {ordine.flussoCommerciale === 'VENDITA' &&
                (ordine.stato === 'CONFERMATO' || ordine.stato === 'IN_LAVORAZIONE') &&
                can('commerciale.ddt', 'edit') && (
                  <button
                    type="button"
                    className="bop-btn-secondary"
                    title="Genera un DDT di vendita dalle righe merce dell'ordine"
                    onClick={() => setModalGeneraDdt(true)}
                  >
                    Genera DDT
                  </button>
                )}
              {ordine.flussoCommerciale === 'APPALTO_CONTRATTO' &&
                (ordine.stato === 'CONFERMATO' || ordine.stato === 'IN_LAVORAZIONE') &&
                can('commerciale.sal', 'edit') && (
                  <button
                    type="button"
                    className="bop-btn-secondary"
                    title="Crea un nuovo SAL sulle righe dell'ordine"
                    onClick={() => navigate(`/commerciale/attivo/sal/nuovo?commessaId=${ordine.commessaId}&ordineId=${ordine.id}`)}
                  >
                    Nuovo SAL
                  </button>
                )}
              {can('commerciale.ordini_clienti', 'delete') && (
                <button
                  type="button"
                  className="bop-btn-danger"
                  disabled={eliminando}
                  title="Elimina l'ordine. Possibile solo se non esistono documenti a valle (distinte, DDT, SAL, contratti, acconti, RDA)"
                  onClick={eliminaOrdine}
                >
                  {eliminando ? 'Elimino…' : 'Elimina ordine'}
                </button>
              )}
            </div>
          </div>
          <h1 className="bop-page-title">Ordine N. {ordine.numero}</h1>
          <div className="bop-scheda-ordine__badges">
            <BadgeStatoOrdine stato={ordine.stato} />
            <BadgeFlussoCommerciale flusso={ordine.flussoCommerciale} />
            {ordine.modalitaEvasione && <BadgeModalitaEvasione modalita={ordine.modalitaEvasione} />}
            {ordine.destinatarioOrdine && <BadgeDestinatario destinatario={ordine.destinatarioOrdine} />}
            {ordine.isSoloImporto && <span className="bop-badge bop-badge--info">Solo importo</span>}
            {haRigheDaEsplodere && (
              <span
                className="bop-badge bop-badge--warning"
                title="Righe in attesa di validazione tecnica (Nodo 5)"
              >
                ⚠ {righeDaEsplodere.length} righe da esplodere
              </span>
            )}
            {haRigheDaConfigurare && (
              <span
                className="bop-badge bop-badge--warning"
                title="Righe configurate con composizione differita in ordine"
              >
                ⚠ {righeDaConfigurare.length} righe da configurare
              </span>
            )}
            {haRigheDaContrattualizzare && (
              <span
                className="bop-badge bop-badge--warning"
                title="Righe ricorrenti in attesa di contrattualizzazione"
              >
                ⚠ {righeDaContrattualizzare.length} righe da contrattualizzare
              </span>
            )}
          </div>
        </header>

        {/* Box acconto — D17 (4 stati: A nessuno / B previsto-no-proforma / C non pagato / D pagato) */}
        {accontoBoxStato !== 'A' && (
          <section
            className={
              accontoBoxStato === 'D'
                ? 'bop-card bop-scheda-ordine__acconto-box bop-scheda-ordine__acconto-box--success'
                : accontoBoxStato === 'C'
                ? 'bop-card bop-scheda-ordine__acconto-box bop-scheda-ordine__acconto-box--danger'
                : 'bop-card bop-scheda-ordine__acconto-box bop-scheda-ordine__acconto-box--warn'
            }
          >
            <div className="bop-scheda-ordine__acconto-box-content">
              {accontoBoxStato === 'B' && (
                <>
                  <div className="bop-scheda-ordine__acconto-box-text">
                    <div className="bop-scheda-ordine__acconto-box-title">
                      Acconto previsto — proforma non generata
                    </div>
                    {ordine.accontoTotaleFreeze && (
                      <div className="bop-scheda-ordine__acconto-box-sub">
                        Importo previsto: {formatCurrency(nz(ordine.accontoTotaleFreeze) ?? 0)}
                        {ordine.accontoPercentualeFreeze && (
                          <> ({formatNumber(nz(ordine.accontoPercentualeFreeze) ?? 0)}%)</>
                        )}
                      </div>
                    )}
                  </div>
                  {can('commerciale.ordini_clienti', 'edit') && (
                    <button
                      type="button"
                      className="bop-btn-primary"
                      disabled={generandoAcconto}
                      onClick={generaProformaAcconto}
                    >
                      {generandoAcconto ? 'Generazione…' : 'Genera proforma acconto'}
                    </button>
                  )}
                </>
              )}

              {accontoBoxStato === 'C' && proformaDaIncassare && (
                <>
                  <div className="bop-scheda-ordine__acconto-box-text">
                    <div className="bop-scheda-ordine__acconto-box-title">
                      ESISTE UN ACCONTO NON PAGATO
                    </div>
                    <div className="bop-scheda-ordine__acconto-box-sub">
                      Proforma {proformaDaIncassare.numero} —{' '}
                      {formatCurrency(nz(proformaDaIncassare.totale) ?? 0)}
                    </div>
                  </div>
                  <Link
                    className="bop-btn-secondary"
                    to={`/commerciale/attivo/acconti/${proformaDaIncassare.id}`}
                  >
                    Apri proforma →
                  </Link>
                </>
              )}

              {accontoBoxStato === 'D' && proformaPagata && (
                <>
                  <div className="bop-scheda-ordine__acconto-box-text">
                    <div className="bop-scheda-ordine__acconto-box-title">
                      Acconto incassato
                    </div>
                    <div className="bop-scheda-ordine__acconto-box-sub">
                      Proforma {proformaPagata.numero} —{' '}
                      {formatCurrency(nz(proformaPagata.totale) ?? 0)}
                      {proformaPagata.dataPagamento && (
                        <> · pagata il {formatDate(proformaPagata.dataPagamento)}</>
                      )}
                    </div>
                  </div>
                  <Link
                    className="bop-btn-secondary"
                    to={`/commerciale/attivo/acconti/${proformaPagata.id}`}
                  >
                    Apri proforma →
                  </Link>
                </>
              )}
            </div>
          </section>
        )}

        {/* Sezione Riepilogo */}
        <section className="bop-card bop-scheda-ordine__section">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Riepilogo</h2>
          </div>
          <div className="bop-card__body">
            <div className="bop-scheda-ordine__griglia">
              <Campo
                label="Data ordine"
                value={
                  <span
                    title={`Creato il ${formatDateTime(ordine.createdAt)} da ${creatoDa}`}
                  >
                    {formatDate(ordine.dataOrdine)}
                  </span>
                }
              />
              <Campo
                label="Data conferma"
                value={
                  ordine.dataConferma
                    ? formatDate(ordine.dataConferma)
                    : <span className="bop-text-muted">(impostata alla conferma)</span>
                }
              />
              <Campo
                label="Data chiusura"
                value={
                  ordine.dataChiusura
                    ? formatDate(ordine.dataChiusura)
                    : <span className="bop-text-muted">(impostata alla chiusura)</span>
                }
              />
              <Campo
                label="Commessa"
                value={
                  ordine.commessa ? (
                    <>
                      <Link
                        to={`/commerciale/commesse/${ordine.commessaId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {ordine.commessa.numero}
                      </Link>
                      {ordine.commessa.descrizione && (
                        <div className="bop-text-muted bop-scheda-ordine__campo-sub">
                          {ordine.commessa.descrizione}
                        </div>
                      )}
                    </>
                  ) : (
                    `#${ordine.commessaId}`
                  )
                }
              />
              <Campo
                label="Preventivo origine"
                value={
                  ordine.preventivoOrigineId ? (
                    <a
                      href={`/commerciale/attivo/preventivi/${ordine.preventivoOrigineId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Apri preventivo origine
                    </a>
                  ) : (
                    '—'
                  )
                }
              />
              <Campo label="Acquirente" value={acquirenteLabel ?? '—'} />
              <Campo label="Descrizione" value={ordine.descrizione ?? '—'} />
              <Campo label="Creato da" value={creatoDa} />
            </div>
            {ordine.note && (
              <div className="bop-scheda-ordine__nota">
                <strong>Note:</strong> {ordine.note}
              </div>
            )}
          </div>
        </section>

        {/* Sezione Cliente (freeze) */}
        <section className="bop-card bop-scheda-ordine__section">
          <div className="bop-card__header">
            <h2 className="bop-card__title">
              Cliente <span className="bop-text-muted">(dati congelati alla creazione dell'ordine)</span>
            </h2>
          </div>
          <div className="bop-card__body">
            <div className="bop-scheda-ordine__griglia">
              <Campo label="Ragione sociale" value={ordine.clienteRagioneSociale} />
              <Campo
                label="Nome / Cognome"
                value={`${ordine.clienteNome ?? ''} ${ordine.clienteCognome ?? ''}`.trim() || null}
              />
              <Campo label="P.IVA" value={ordine.clientePartitaIva} />
              <Campo label="Codice fiscale" value={ordine.clienteCodiceFiscale} />
              <Campo label="Indirizzo" value={ordine.clienteIndirizzo} />
              <Campo label="CAP" value={ordine.clienteCap} />
              <Campo label="Città" value={ordine.clienteCitta} />
              <Campo label="Provincia" value={ordine.clienteProvincia} />
              <Campo label="Email" value={ordine.clienteEmail} />
              <Campo label="Telefono" value={ordine.clienteTelefono} />
              <Campo label="Indirizzo lavori" value={ordine.indirizzoLavori} colspan={2} />
            </div>
          </div>
        </section>

        {/* Sezione Condizioni */}
        <section className="bop-card bop-scheda-ordine__section">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Condizioni</h2>
          </div>
          <div className="bop-card__body">
            <div className="bop-scheda-ordine__griglia">
              <Campo
                label="Codice IVA default"
                value={
                  ordine.codiceIvaDefault
                    ? `${ordine.codiceIvaDefault.codice} — ${ordine.codiceIvaDefault.descrizione ?? ''} (${formatNumber(Number(ordine.codiceIvaDefault.aliquota))} %)`
                    : `#${ordine.codiceIvaDefaultId}`
                }
              />
              <Campo
                label="Condizione di pagamento"
                value={ordine.condizionePagamento?.nome ?? '—'}
              />
            </div>
          </div>
        </section>

        {/* Sezione Righe */}
        <section className="bop-card bop-scheda-ordine__section">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Righe ({ordine.righe.length})</h2>
          </div>
          <div className="bop-card__body">
            <TabellaRigheGerarchica
              raggruppamenti={ordine.raggruppamenti}
              righe={ordine.righe}
              expandedRighe={expandedRighe}
              onToggleRiga={toggleExpansion}
              canEsplodi={can('commerciale.ordini_clienti', 'edit') && ordine.stato === 'BOZZA'}
              esplosioneBloccata={!!proformaDaIncassare}
              motivoBloccoEsplosione={
                proformaDaIncassare
                  ? 'Acconto da conversione non ancora pagato: esplosione bloccata'
                  : null
              }
              onEsplodi={(r) => setEsplodiRiga(r)}
              canConfigura={can('commerciale.ordini_clienti', 'edit') && ordine.stato === 'BOZZA'}
              configurazioneBloccata={!!proformaDaIncassare}
              motivoBloccoConfigurazione={
                proformaDaIncassare
                  ? 'Acconto da conversione non ancora pagato: configurazione bloccata'
                  : null
              }
              onConfigura={(r) => setConfiguraRiga(r)}
              canContrattualizza={can('commerciale.ordini_clienti', 'edit') && ordine.stato === 'BOZZA'}
              contrattualizzazioneBloccata={!!proformaDaIncassare}
              motivoBloccoContrattualizzazione={
                proformaDaIncassare
                  ? 'Acconto da conversione non ancora pagato: contrattualizzazione bloccata'
                  : null
              }
              onContrattualizza={(r) => setContrattualizzaRiga(r)}
              onAnnullaContrattualizzazione={(r) => annullaContrattualizzazione(r)}
              onVediContratto={(cid) => navigate(`/commerciale/attivo/contratti/${cid}`)}
            />
          </div>
        </section>

        {/* Sezione Totali */}
        <section className="bop-card bop-scheda-ordine__section">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Totali</h2>
          </div>
          <div className="bop-card__body">
            <div className="bop-scheda-ordine__totali">
              {(() => {
                const imponibileNetto = nz(ordine.totaleImponibile) ?? 0;
                const sconto = nz(ordine.scontoGlobale);
                const hasSconto = sconto != null && sconto !== 0;
                // Subtotale lordo = imponibile netto - sconto (sconto è negativo)
                const subtotaleLordo = hasSconto ? imponibileNetto - sconto : imponibileNetto;
                const aliquotaIva = ordine.codiceIvaDefault?.aliquota != null
                  ? Number(ordine.codiceIvaDefault.aliquota)
                  : null;
                const totaleOrdine = nz(ordine.totale) ?? 0;
                const accontoTotale = nz(ordine.accontoTotaleFreeze) ?? 0;
                const mostraAcconto = ordine.accontoPrevisto && ordine.accontoTotaleFreeze != null;
                const saldo = totaleOrdine - accontoTotale;

                return (
                  <>
                    {hasSconto && (
                      <>
                        <div className="bop-scheda-ordine__totali-row">
                          <span>Subtotale righe</span>
                          <strong>{formatCurrency(subtotaleLordo)}</strong>
                        </div>
                        <div className="bop-scheda-ordine__totali-row bop-scheda-ordine__totali-row--sconto">
                          <span>
                            Sconto globale
                            {ordine.scontoGlobaleDescrizione && (
                              <span className="bop-text-muted"> — {ordine.scontoGlobaleDescrizione}</span>
                            )}
                          </span>
                          <strong>{formatCurrency(sconto)}</strong>
                        </div>
                      </>
                    )}
                    <div className="bop-scheda-ordine__totali-row">
                      <span>{hasSconto ? 'Imponibile netto' : 'Imponibile'}</span>
                      <strong>{formatCurrency(imponibileNetto)}</strong>
                    </div>
                    <div className="bop-scheda-ordine__totali-row">
                      <span>
                        IVA
                        {aliquotaIva != null && (
                          <span className="bop-text-muted"> (al {formatNumber(aliquotaIva)}%)</span>
                        )}
                      </span>
                      <strong>{formatCurrency(nz(ordine.totaleIva))}</strong>
                    </div>
                    <div className="bop-scheda-ordine__totali-row bop-scheda-ordine__totali-row--grande">
                      <span>Totale</span>
                      <strong>{formatCurrency(totaleOrdine)}</strong>
                    </div>
                    {mostraAcconto && (
                      <>
                        <div className="bop-scheda-ordine__totali-row bop-scheda-ordine__totali-row--acconto">
                          <span>
                            Acconto
                            {ordine.accontoPercentualeFreeze != null && (
                              <span className="bop-text-muted"> ({formatNumber(nz(ordine.accontoPercentualeFreeze) ?? 0)}%)</span>
                            )}
                          </span>
                          <strong>{formatCurrency(accontoTotale)}</strong>
                        </div>
                        <div className="bop-scheda-ordine__totali-row">
                          <span className="bop-text-muted">Imponibile acconto + IVA</span>
                          <span className="bop-text-muted">
                            {formatCurrency(nz(ordine.accontoImponibileFreeze))} + {formatCurrency(nz(ordine.accontoIvaFreeze))}
                          </span>
                        </div>
                        {ordine.accontoCondPagamento && (
                          <div className="bop-scheda-ordine__totali-row">
                            <span className="bop-text-muted">Pagamento acconto</span>
                            <span className="bop-text-muted">{ordine.accontoCondPagamento.nome}</span>
                          </div>
                        )}
                        <div className="bop-scheda-ordine__totali-row">
                          <span>Saldo</span>
                          <strong>{formatCurrency(saldo)}</strong>
                        </div>
                      </>
                    )}
                    {hasSconto && (
                      <div className="bop-scheda-ordine__totali-nota bop-text-muted">
                        Lo sconto è incluso nei totali sopra.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Sezione Distinte — solo appalto/contratto, ordine non in bozza */}
        {ordine.flussoCommerciale === 'APPALTO_CONTRATTO' &&
          ordine.stato !== 'BOZZA' &&
          can('commerciale.distinte', 'view') && <SezioneDistinte ordine={ordine} />}

        {/* Sezione Evasione — DDT e SAL generati dall'ordine */}
        {ordine.stato !== 'BOZZA' &&
          (can('commerciale.ddt', 'view') || can('commerciale.sal', 'view')) && (
            <SezioneEvasione ordine={ordine} />
          )}

        {/* Sezione Allegati */}
        <section className="bop-card bop-scheda-ordine__section">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Allegati</h2>
          </div>
          <div className="bop-card__body">
            <ListaAllegati
              ordineId={ordine.id}
              allegati={ordine.allegati}
              canEdit={true}
              onChanged={reload}
            />
          </div>
        </section>

        {/* Sezione Log */}
        <section className="bop-card bop-scheda-ordine__section">
          <div className="bop-card__header">
            <h2 className="bop-card__title">Log</h2>
          </div>
          <div className="bop-card__body">
            <TabellaLog log={logEntries} />
          </div>
        </section>

        {esplodiRiga && (
          <ModalEsplosioneRiga
            ordineId={ordine.id}
            rigaId={esplodiRiga.id}
            forfait={Number(esplodiRiga.importoNetto ?? 0)}
            componentiIniziali={esplodiRiga.componenti}
            societaId={ordine.societaId}
            onClose={() => setEsplodiRiga(null)}
            onSaved={async () => {
              setEsplodiRiga(null);
              await reload();
            }}
          />
        )}

        {configuraRiga && (
          <ModalConfigurazioneRigaOrdine
            ordineId={ordine.id}
            societaId={ordine.societaId}
            riga={configuraRiga}
            onClose={() => setConfiguraRiga(null)}
            onSaved={async () => {
              setConfiguraRiga(null);
              await reload();
            }}
          />
        )}

        {contrattualizzaRiga && (
          <ModalContrattualizzazioneRiga
            ordineId={ordine.id}
            societaId={ordine.societaId}
            riga={contrattualizzaRiga}
            onClose={() => setContrattualizzaRiga(null)}
            onSaved={async () => {
              setContrattualizzaRiga(null);
              await reload();
            }}
          />
        )}

        {modalGeneraDdt && (
          <ModalGeneraDdt
            tipo="VENDITA"
            ordineId={ordine.id}
            onClose={() => setModalGeneraDdt(false)}
            onCreated={(ddtId) => navigate(`/commerciale/attivo/ddt/${ddtId}`)}
          />
        )}

        {modalStampa && (
          <PrintModal
            societaId={ordine.societaId}
            entitaType="ordine_cliente"
            entitaId={ordine.id}
            entitaLabel={`Ordine ${ordine.numero}`}
            onClose={() => setModalStampa(false)}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
};

export default SchedaOrdine;
